import {
  formatPhoneNumbers,
  formattedAccountDetails,
  formattedDirectDebitPayment,
  formattedUpdatedBillingPreferences,
} from "queries/customer.utils";
import { QueryFunctionContext } from "react-query";
import { JwtClient } from "utils/jwtClient";

export const getAccountInfo = async ({
  queryKey,
}: QueryFunctionContext<
  [string, { jwtClient: JwtClient; accountOverrideNumber?: string }]
>): Promise<AccountInfo[]> => {
  const [_, { jwtClient, accountOverrideNumber }] = queryKey;
  const response = await jwtClient.apiCall({
    path: "/customer/user_accounts_info",
    queryParams: {
      account_number: accountOverrideNumber,
    },
  });

  const { data } = await response.json();
  return data;
};

export const getAccountDetails = async ({
  queryKey,
}: QueryFunctionContext<
  [string, { accountNumber: string; jwtClient: JwtClient }]
>): Promise<AccountDetails> => {
  const [_, { accountNumber, jwtClient }] = queryKey;
  const response = await jwtClient.apiCall({
    path: `/customer/accounts/${accountNumber}`,
  });
  const data = await response.json();

  return formattedAccountDetails(data);
};

export const closeAccount = async (
  accountNumber: string,
  jwtClient: JwtClient,
  endDate: string,
): Promise<AccountInfo> => {
  const response = await jwtClient.apiCall({
    path: `/customer/accounts/account_closure/`,
    queryParams: {
      account_identifier: accountNumber,
      end_date: endDate,
    },
    fetchParams: {
      method: "PUT",
    },
  });
  const { data } = await response.json();
  return data;
};

export type MovingHouseProps = {
  accountNumber: string;
  address: CustomerAddress;
  icpNumber: string;
  jwtClient: JwtClient;
  moveInDate: string;
  moveOutDate?: string;
  medicalDependency: boolean;
  termsAccepted: boolean;
  usageType: string;
};

export const movingHouse = async ({
  accountNumber,
  address,
  icpNumber,
  jwtClient,
  moveInDate,
  moveOutDate,
  medicalDependency,
  termsAccepted,
  usageType,
}: MovingHouseProps): Promise<AccountInfo> => {
  // Address and icp number are mutually exclusive on the api
  const hasAddress = address.city && address.number && address.street;
  const queryParams = {
    account_identifier: accountNumber,
    "address[city]": hasAddress ? address.city : undefined,
    "address[number]": hasAddress ? address.number : undefined,
    "address[postcode]": hasAddress ? address.postcode : undefined,
    "address[street]": hasAddress ? address.street : undefined,
    "address[suburb]": hasAddress ? address.suburb : undefined,
    "address[unit]": hasAddress ? address.unit : undefined,
    icp_number: hasAddress ? undefined : icpNumber,
    move_in_date: moveInDate,
    move_out_date: moveOutDate,
    medical_dependency: medicalDependency,
    terms_accepted: termsAccepted,
    usage_type: usageType,
  };

  const response = await jwtClient.apiCall({
    path: `/customer/moving_house`,
    queryParams: Object.entries(queryParams).reduce((acc, [key, val]) => {
      // Remove the undefined values. Can't use `if (val)` as some values are boolean and can be false
      if (val !== undefined) {
        return { ...acc, [key]: val };
      }
      return acc;
    }, {}),
    fetchParams: {
      method: "POST",
    },
  });

  const { data } = await response.json();
  return data;
};

export type AddDirectDebitPaymentProps = {
  accountIdentifier: string;
  accountName: string;
  agreedToTerms: boolean;
  bankAccountNumber: string;
  code?: string;
  confirmedAuthority: boolean;
  jwtClient: JwtClient;
  particulars?: string;
  reference?: string;
  isDefaultPaymentMethod?: boolean; // this is not implemented in the UI. Adding it to the query in case of future use. Defaults to true in API
};

export const addDirectDebitPayment = async ({
  accountIdentifier,
  accountName,
  agreedToTerms,
  bankAccountNumber,
  code,
  confirmedAuthority,
  jwtClient,
  particulars,
  reference,
  isDefaultPaymentMethod = false,
}: AddDirectDebitPaymentProps): Promise<AccountDetailPaymentMethod> => {
  const body = {
    account_name: accountName,
    agreed_to_terms: agreedToTerms,
    account_number: bankAccountNumber,
    code,
    confirmed_bank_authority: confirmedAuthority,
    particulars,
    reference,
    default_payment_method: isDefaultPaymentMethod,
  };

  const response = await jwtClient.apiCall({
    path: `/customer/direct_debit/${accountIdentifier}`,
    params: {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
    fetchParams: {
      body: JSON.stringify(body),
      method: "POST",
    },
  });

  const { data } = await response.json();
  return formattedDirectDebitPayment(data);
};

type AddCreditCardPaymentProps = {
  jwtClient: JwtClient;
  redirectUrl: string;
  accountIdentifier: string;
};

/**
   Queries the add card API which redirects to Windcave.
   query body params:
 * @param accountIdentifier: account number
 * @param redirectUrl: The URL to redirect to when returning  from Windcave
 */
export const addCreditCardPayment = async ({
  accountIdentifier,
  redirectUrl,
  jwtClient,
}: AddCreditCardPaymentProps) => {
  const body = {
    account_identifier: accountIdentifier,
    redirect_url: redirectUrl,
  };

  const response = await jwtClient.apiCall({
    path: "/customer/authenticated_credit_cards/authorisations/",
    params: {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
    fetchParams: {
      body: JSON.stringify(body),
      method: "POST",
    },
  });

  const { data } = await response.json();
  return data;
};

/**
 * Gets the result of the new card authorisation from Windcave
 * @param windcaveTransactionId: id received from windcave  in the addCreditCardPayment response
 */
export const getWindcaveAuthResult = async ({
  windcaveTransactionId,
  jwtClient,
}): Promise<AddCardAuthorisationResponse> => {
  const response = await jwtClient.apiCall({
    path: `/customer/authenticated_credit_cards/authorisations/${windcaveTransactionId}`,
  });

  const { data } = await response.json();
  return data;
};

export type UpdatePhoneNumbersProps = {
  jwtClient: JwtClient;
  accountIdentifier: string;
  params: {
    contactId: number;
    mobileNumberParams?: { id: number; number: string; type: string }[];
    homeNumberParams?: { id: number; number: string; type: string }[];
  };
};

export const updatePhoneNumbers = async ({
  accountIdentifier,
  jwtClient,
  params: { contactId, mobileNumberParams, homeNumberParams },
}: UpdatePhoneNumbersProps) => {
  const body = {
    phone_number_attributes: [
      ...formatPhoneNumbers(mobileNumberParams),
      ...formatPhoneNumbers(homeNumberParams),
    ],
  };

  const response = await jwtClient.apiCall({
    path: `/customer/contacts/${accountIdentifier}`,
    params: {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    },
    queryParams: { contact_id: contactId },
    fetchParams: {
      body: JSON.stringify(body),
      method: "PUT",
    },
  });

  const { data } = await response.json();
  return data;
};

export type UpdateBillingPreferencesProps = {
  jwtClient: JwtClient;
  params: {
    accountIdentifier: string;
    billingEmail?: string;
    billingPeriod?: string;
    billingPeriodOffsetWeeks?: string;
    selectedPaymentMethod?: AccountDetailPaymentMethod;
  };
};

/**
 * a newly added payment method has pament_type in snake_case, but the existing ones
 * fetched on load are PascalCase and need to use this map
 * */
const defaultPaymentTypes = {
  CreditCard: "credit_card",
  DirectDebit: "direct_debit",
};

export const updateBillingPreferences = async ({
  params: {
    accountIdentifier,
    billingEmail,
    billingPeriod,
    billingPeriodOffsetWeeks,
    selectedPaymentMethod,
  },
  jwtClient,
}: UpdateBillingPreferencesProps) => {
  const params = {
    account_identifier: accountIdentifier,
    ...(billingEmail && { billing_email: billingEmail }),
    ...(billingPeriod && {
      billing_period: billingPeriod,
    }),
    ...(billingPeriodOffsetWeeks && {
      billing_period_offset_weeks: billingPeriodOffsetWeeks,
    }),
    ...(selectedPaymentMethod && {
      default_payment_method_id: selectedPaymentMethod.payment_method_id,
      default_payment_method_type:
        defaultPaymentTypes[selectedPaymentMethod.payment_type] ??
        selectedPaymentMethod.payment_type,
    }),
  };

  const response = await jwtClient.apiCall({
    path: "/customer/billing_preferences",
    queryParams: params,
    fetchParams: {
      method: "PUT",
    },
  });

  const { data } = await response.json();
  return formattedUpdatedBillingPreferences(data);
};
